import { useState } from "react";
import { useTheme } from "@mui/material";
import { getFileTitle } from "files/hooks/useFileTitle";
import useFiles, { resolveRouterPath, useFilesSearch } from "files/hooks/useFiles";
import FileJob, { JobStatus, JobType } from "files/types/interfaces/fileJob";
import { SWRResponse } from "swr";
import { StringParam, useQueryParam } from "use-query-params";
import { useFetcher } from "~/hooks/useFetcher";
import useProjectsAccessList, { ProjectRolePrivilege } from "~/hooks/useProjectsAccessList";

const { VITE_RWB_API } = import.meta.env;

interface ToastMessage {
  message: string;
  visible: boolean;
  undoCallback?: () => Promise<void>;
}

const getCompletedMessage = (
  job: FileJob,
  projectAccess: SWRResponse<ProjectRolePrivilege[]>
): string => {
  const verbs: Partial<Record<JobType, string>> = {
    [JobType.COPY]: "Copied",
    [JobType.RENAME]: "Renamed",
    [JobType.MOVE]: "Moved",
  };

  const sourcePath = resolveRouterPath(job.source);
  const formattedSource = sourcePath.split("/").filter(Boolean).at(-1);
  const destPath = job.destination;
  const destTitleKey = destPath
    .split("/")
    .filter(Boolean)
    // Use new file/folder name if rename, otherwise use parent directory for move/copy
    .slice(0, job.job_type === JobType.RENAME ? undefined : -1)
    .join("/");

  const { title: formattedDest } = getFileTitle(destTitleKey, true, projectAccess);

  return `${verbs[job.job_type]} "${formattedSource}" to "${formattedDest ?? ""}"`;
};

/**
 * Manages file operations (copy, move, rename) and their status notifications.
 *
 * Key features:
 * - Adaptive polling frequency based on job status
 * - Toast notifications for completed operations
 * - Undo functionality for move and rename operations
 *
 * @param prefix - Explicit directory prefix to revalidate on job completion. Pass this for
 *   callers (like PersonalDatasetFiles) that track the browsed folder as local state rather
 *   than the URL, since `useFiles()` otherwise resolves the current directory from the route.
 * @returns {Object} SWR response properties plus a notification object
 *   with message, visibility state, and hide function
 *
 * @example
 * const { notification } = useFileJobs();
 */
const useFileJobs = (prefix?: string) => {
  const { transitions } = useTheme();
  const { mutate: mutateFiles } = useFiles(prefix);
  const projectAccess = useProjectsAccessList();
  const { mutate: mutateFilesSearch } = useFilesSearch();
  const [search] = useQueryParam("q", StringParam);
  const [previousResp, setPreviousResp] = useState<FileJob[]>([]);
  const [toastMessage, setToastMessage] = useState<ToastMessage>({
    message: "",
    visible: false,
  });

  const hasPendingJobs = previousResp.some((job) =>
    [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status)
  );

  const jobsResp = useFetcher<FileJob[]>(
    `${VITE_RWB_API}/file-browser/jobs?include_all_recent=true`,
    {
      // Poll at more frequent rate if previous resp has actively running jobs
      refreshInterval: hasPendingJobs ? 1000 : 5000,
      onSuccess: async (data: FileJob[]) => {
        const previouslyPendingIds = previousResp
          .filter(({ status }) => [JobStatus.PENDING, JobStatus.RUNNING].includes(status))
          .map(({ id }) => id);
        const jobsRecentlyCompleted = data.filter(
          ({ id, status }) => status === JobStatus.COMPLETED && previouslyPendingIds.includes(id)
        );

        setPreviousResp(data);

        if (jobsRecentlyCompleted.length) {
          // Update file rows to show actions instead of pending message / disabled state
          if (search) {
            await mutateFilesSearch();
          }

          await mutateFiles();

          for (let i = 0; i < jobsRecentlyCompleted.length; i++) {
            const job = jobsRecentlyCompleted[i];
            const { job_type, created_by, destination: undoSource, source: undoDest } = job;
            setToastMessage({
              visible: true,
              message: getCompletedMessage(job, projectAccess),
              undoCallback: [JobType.MOVE, JobType.RENAME].includes(job_type)
                ? async () => {
                    const undoResp = await fetch(`${VITE_RWB_API}/file-browser/jobs`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        job_type: job_type,
                        source: undoSource,
                        dest: undoDest,
                      }),
                    });

                    if (undoResp.ok) {
                      const id = await undoResp.json();
                      // Optimistically update pending jobs
                      await jobsResp.mutate([
                        ...(data ?? []),
                        {
                          id,
                          source: undoSource,
                          destination: undoDest,
                          status: JobStatus.PENDING,
                          created_by,
                          job_type,
                        },
                      ]);

                      if (search) {
                        await mutateFilesSearch();
                      }

                      await mutateFiles();
                    }
                  }
                : undefined,
            });

            if (jobsRecentlyCompleted.length > 1) {
              // If there are multiple completing in the same response,
              // delay showing next snackbar message until timeout is over
              await new Promise((resolve) => setTimeout(resolve, 3500));
            }
          }
        }
      },
      keepPreviousData: true,
    }
  );

  return {
    ...jobsResp,
    notification: {
      ...toastMessage,
      hide: () => {
        // Trigger visibility change
        setToastMessage((prev) => ({ ...prev, visible: false }));
        // Clear message after timeout to avoid blank state
        setTimeout(
          () => setToastMessage({ message: "", visible: false }),
          transitions.duration.leavingScreen
        );
      },
    },
  };
};

export default useFileJobs;
